/**
 * CreditConfirmModal Component
 * 
 * Analysis credit usage confirmation modal.
 * Fully internationalized with i18n support.
 */

import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

export const CreditConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  onUpgrade,
  estimatedCredits,
  userCredits,
  loading = false
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const hasEnoughCredits = (userCredits - estimatedCredits) >= 0;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      data-testid="credit-confirm-modal"
    >
      <Card className="bg-gray-900 border-2 border-[#FFDD59] max-w-md w-full">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-[#FFDD59] mb-4 text-center">
            {t('modal.confirm_title')}
          </h3>
          
          <div className="space-y-4">
            <p className="text-white text-center text-lg">
              {t('modal.credit_cost', { credits: estimatedCredits })}
            </p>
            
            {!hasEnoughCredits ? (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-sm">
                  {t('modal.insufficient_credits')}
                </p>
              </div>
            ) : (
              <p className="text-gray-300 text-sm text-center">
                {t('modal.confirm_desc')}
              </p>
            )}
            
            <div className="flex gap-4">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                data-testid="credit-cancel-btn"
              >
                {t('modal.cancel')}
              </Button>
              
              {hasEnoughCredits ? (
                <Button
                  onClick={onConfirm}
                  disabled={loading}
                  className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                  data-testid="credit-confirm-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('modal.sending')}
                    </>
                  ) : (
                    t('modal.confirm_send')
                  )}
                </Button>
              ) : (
                <Button
                  onClick={onUpgrade}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                  data-testid="credit-upgrade-btn"
                >
                  {t('modal.upgrade')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditConfirmModal;
